import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { 
  ArrowLeft, 
  ArrowRight,
  Plus,
  Trash2,
  Calendar,
  FileText,
  ListChecks,
  Settings,
  Upload,
  Image as ImageIcon,
  X,
  Loader2,
  Info,
  ShieldCheck,
  Eye,
  Users,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Layout } from "../components/Layout";
import { createConsulta, uploadImage, getDpaStatus } from "../lib/api";
import DPAAcceptanceFlow from "../components/DPAAcceptanceFlow";
import { toast } from "sonner";
import { format } from "date-fns";
import { ca, es, eu, gl } from "date-fns/locale";
import { API_URL } from "../lib/config";
import { MarkdownHelp } from "../components/MarkdownHelp";

const dateFnsLocales = { ca, es, eu, gl };

export default function NewConsultaPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const currentLocale = dateFnsLocales[i18n.language] || ca;
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploadingIndex, setUploadingIndex] = useState(null);
  const [uploadingConsultaImage, setUploadingConsultaImage] = useState(false);
  const consultaImageRef = useRef(null);
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [dpaAccepted, setDpaAccepted] = useState(null); // null = checking, false = needs DPA, true = OK
  const [endDateOpen, setEndDateOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    eligibility: "",
    image_url: null,
    image_urls: [],
    options: [
      { title: "", description: "", image_url: null, image_urls: [], amount: null },
      { title: "", description: "", image_url: null, image_urls: [], amount: null }
    ],
    startDate: null,
    startTime: "09:00",
    endDate: null,
    endTime: "21:00",
    requireRegistration: false,
    enablePrivateAudit: false,
    enableVotMaAlcada: false,
    enableAudit: false,
    enableVoterTracking: false,
    enableStatsSex: true,
    enableStatsAge: true,
    // Voting restriction settings
    restrictionType: "options", // 'options' or 'amount'
    minOptions: 1,
    maxOptions: 1,
    maxTotalAmount: ""
  });

  const fileInputRefs = useRef({});

  useEffect(() => {
    const checkDpa = async () => {
      try {
        const { data } = await getDpaStatus();
        setDpaAccepted(data.accepted);
      } catch {
        setDpaAccepted(true); // If check fails, don't block
      }
    };
    checkDpa();
  }, []);

  const handleOptionChange = (index, field, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = { ...newOptions[index], [field]: value };
    setFormData({ ...formData, options: newOptions });
  };

  const handleImageUpload = async (index, file) => {
    if (!file) return;
    
    // Validate file
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      toast.error(t('consultaDetail.formatNotAllowed'));
      return;
    }
    
    if (file.size > 20 * 1024 * 1024) {
      toast.error(t('consultaDetail.fileTooLarge'));
      return;
    }
    
    setUploadingIndex(index);
    try {
      const response = await uploadImage(file);
      const newUrl = response.data.url;
      const currentUrls = formData.options[index].image_urls || [];
      if (currentUrls.length >= 6) {
        toast.error(t('consultaDetail.maxImagesReached'));
        return;
      }
      const updatedUrls = [...currentUrls, newUrl];
      const newOptions = [...formData.options];
      newOptions[index] = { ...newOptions[index], image_urls: updatedUrls, image_url: updatedUrls[0] };
      setFormData({ ...formData, options: newOptions });
      toast.success(t('consultaDetail.imageUploaded'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('consultaDetail.imageUploadError'));
    } finally {
      setUploadingIndex(null);
    }
  };

  const handleConsultaImageUpload = async (file) => {
    if (!file) return;
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type)) { toast.error(t('consultaDetail.formatNotAllowed')); return; }
    if (file.size > 20 * 1024 * 1024) { toast.error(t('consultaDetail.fileTooLarge')); return; }
    const currentUrls = formData.image_urls || [];
    if (currentUrls.length >= 6) { toast.error(t('consultaDetail.maxImagesReached')); return; }
    setUploadingConsultaImage(true);
    try {
      const response = await uploadImage(file);
      const newUrl = response.data.url;
      const updatedUrls = [...currentUrls, newUrl];
      setFormData({ ...formData, image_urls: updatedUrls, image_url: updatedUrls[0] });
      toast.success(t('consultaDetail.imageUploaded'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('consultaDetail.imageUploadError'));
    } finally {
      setUploadingConsultaImage(false);
    }
  };

  const removeImage = (index, imgIdx) => {
    if (imgIdx !== undefined) {
      const currentUrls = [...(formData.options[index].image_urls || [])];
      currentUrls.splice(imgIdx, 1);
      const newOptions = [...formData.options];
      newOptions[index] = { ...newOptions[index], image_urls: currentUrls, image_url: currentUrls[0] || null };
      setFormData({ ...formData, options: newOptions });
    } else {
      const newOptions = [...formData.options];
      newOptions[index] = { ...newOptions[index], image_urls: [], image_url: null };
      setFormData({ ...formData, options: newOptions });
    }
  };

  const removeConsultaImage = (imgIdx) => {
    const currentUrls = [...(formData.image_urls || [])];
    currentUrls.splice(imgIdx, 1);
    setFormData({ ...formData, image_urls: currentUrls, image_url: currentUrls[0] || null });
  };

  const addOption = () => {
    setFormData({ 
      ...formData, 
      options: [...formData.options, { title: "", description: "", image_url: null, image_urls: [], amount: null }] 
    });
  };

  const removeOption = (index) => {
    if (formData.options.length > 2) {
      const newOptions = formData.options.filter((_, i) => i !== index);
      setFormData({ ...formData, options: newOptions });
    }
  };

  const validateStep = (stepNum) => {
    switch (stepNum) {
      case 1:
        if (!formData.title.trim()) {
          toast.error(t('consultaDetail.titleRequired'));
          return false;
        }
        if (!formData.description.trim()) {
          toast.error(t('newConsulta.descRequired'));
          return false;
        }
        return true;
      case 2:
        const validOptions = formData.options.filter(o => o.title.trim());
        if (validOptions.length < 2) {
          toast.error(t('consultaDetail.minOptionsWithTitle'));
          return false;
        }
        return true;
      case 3:
        if (!formData.startDate || !formData.endDate) {
          toast.error(t('consultaDetail.datesRequired'));
          return false;
        }
        // Combine date and time for comparison
        const startDateTime = new Date(formData.startDate);
        const [startHour, startMin] = formData.startTime.split(':');
        startDateTime.setHours(parseInt(startHour), parseInt(startMin), 0, 0);
        
        const endDateTime = new Date(formData.endDate);
        const [endHour, endMin] = formData.endTime.split(':');
        endDateTime.setHours(parseInt(endHour), parseInt(endMin), 0, 0);
        
        if (startDateTime >= endDateTime) {
          toast.error(t('consultaDetail.endAfterStart'));
          return false;
        }
        return true;
      case 4: {
        const totalOptions = formData.options.filter(o => o.title.trim()).length;
        if (formData.restrictionType === 'options') {
          const maxOpt = parseInt(formData.maxOptions) || 1;
          const minOpt = parseInt(formData.minOptions) || 1;
          if (maxOpt > totalOptions) {
            toast.error(t('newConsulta.maxOptionsError', { max: maxOpt, total: totalOptions }));
            return false;
          }
          if (minOpt > maxOpt) {
            toast.error(t('newConsulta.minMaxError'));
            return false;
          }
        }
        if (formData.restrictionType === 'amount') {
          const maxAmount = parseFloat(formData.maxTotalAmount);
          if (!maxAmount || maxAmount <= 0) {
            toast.error(t('newConsulta.invalidMaxAmount'));
            return false;
          }
          const optionsWithHighAmount = formData.options
            .filter(o => o.title.trim() && parseFloat(o.amount) > maxAmount);
          if (optionsWithHighAmount.length > 0) {
            const names = optionsWithHighAmount.map(o => `"${o.title}"`).join(', ');
            toast.error(t('newConsulta.amountExceedsError', { names, max: maxAmount }));
            return false;
          }
        }
        return true;
      }
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(step)) return;

    setLoading(true);
    try {
      // Combine date and time
      const startDateTime = new Date(formData.startDate);
      const [startHour, startMin] = formData.startTime.split(':');
      startDateTime.setHours(parseInt(startHour), parseInt(startMin), 0, 0);
      
      const endDateTime = new Date(formData.endDate);
      const [endHour, endMin] = formData.endTime.split(':');
      endDateTime.setHours(parseInt(endHour), parseInt(endMin), 0, 0);
      
      const pad = (n) => String(n).padStart(2, '0');
      const toLocalISO = (dt) => `${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}:00`;

      const data = {
        title: formData.title,
        description: formData.description,
        eligibility: formData.eligibility || null,
        image_url: formData.image_url || null,
        image_urls: formData.image_urls?.length ? formData.image_urls : null,
        options: formData.options
          .filter(o => o.title.trim())
          .map(o => ({
            title: o.title.trim(),
            description: o.description?.trim() || null,
            image_url: o.image_url || null,
            image_urls: o.image_urls?.length ? o.image_urls : null,
            amount: o.amount ? parseFloat(o.amount) : null
          })),
        start_date: toLocalISO(startDateTime),
        end_date: toLocalISO(endDateTime),
        require_registration: formData.requireRegistration,
        enable_private_audit: formData.enablePrivateAudit,
        enable_vot_ma_alcada: formData.enableVotMaAlcada,
        enable_audit: formData.enableAudit,
        enable_voter_tracking: formData.enableVoterTracking,
        enable_stats_sex: formData.enableStatsSex,
        enable_stats_age: formData.enableStatsAge,
        // Voting restriction settings
        restriction_type: formData.restrictionType,
        min_options: parseInt(formData.minOptions) || 1,
        max_options: parseInt(formData.maxOptions) || 1,
        max_total_amount: formData.maxTotalAmount ? parseInt(formData.maxTotalAmount) : null
      };

      const response = await createConsulta(data);
      toast.success(t('newConsulta.createdSuccess'));
      navigate(`/consultes/${response.data.id}/census`);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('newConsulta.createError'));
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { num: 1, label: t('newConsulta.stepInfo'), icon: FileText },
    { num: 2, label: t('newConsulta.stepProposals'), icon: ListChecks },
    { num: 3, label: t('newConsulta.stepDates'), icon: Calendar },
    { num: 4, label: t('newConsulta.stepConfig'), icon: Settings }
  ];

  // Show DPA flow if not accepted
  if (dpaAccepted === null) {
    return (
      <Layout>
        <div className="content-container max-w-3xl flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
        </div>
      </Layout>
    );
  }

  if (dpaAccepted === false) {
    return (
      <Layout>
        <div className="content-container max-w-4xl" data-testid="dpa-flow-container">
          <Button
            variant="ghost"
            onClick={() => navigate("/consultes")}
            className="mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('newConsulta.previous')}
          </Button>
          <DPAAcceptanceFlow onComplete={() => setDpaAccepted(true)} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="content-container max-w-3xl" data-testid="new-consulta-page">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Button
            variant="ghost"
            onClick={() => navigate("/consultes")}
            className="mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('newConsulta.previous')}
          </Button>
          <h1 className="text-3xl font-heading font-bold text-slate-900">
            {t('newConsulta.title')}
          </h1>
          <p className="text-slate-500 mt-1">
            {t('newConsulta.subtitle')}
          </p>
        </motion.div>

        {/* Progress Steps */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex items-center justify-between mb-8 px-4"
        >
          {steps.map((s, index) => (
            <div key={s.num} className="flex items-center">
              <div className="wizard-step">
                <div className={`wizard-step-number ${
                  step > s.num ? "completed" : step === s.num ? "active" : "pending"
                }`}>
                  {step > s.num ? "✓" : s.num}
                </div>
                <span className={`hidden sm:inline text-sm ${
                  step >= s.num ? "text-slate-900 font-medium" : "text-slate-400"
                }`}>
                  {s.label}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div className={`h-0.5 w-8 sm:w-16 mx-2 ${
                  step > s.num ? "bg-emerald-500" : "bg-slate-200"
                }`} />
              )}
            </div>
          ))}
        </motion.div>

        {/* Form */}
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="bg-white border border-slate-200 rounded-lg p-8"
        >
          {/* Step 1: Basic Info */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="form-group">
                <Label htmlFor="title" className="form-label">
                  {t('newConsulta.consultaTitle')} *
                </Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder={t('newConsulta.titlePlaceholder')}
                  className="h-11"
                  data-testid="consulta-title"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="description" className="form-label">
                  {t('newConsulta.description')} *
                  <span className="ml-2"><MarkdownHelp /></span>
                </Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('newConsulta.descPlaceholder')}
                  rows={4}
                  data-testid="consulta-description"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="eligibility" className="form-label">
                  {t('newConsulta.eligibility')}
                </Label>
                <Textarea
                  id="eligibility"
                  value={formData.eligibility}
                  onChange={(e) => setFormData({ ...formData, eligibility: e.target.value })}
                  placeholder={t('newConsulta.eligibilityPlaceholder')}
                  rows={3}
                  data-testid="consulta-eligibility"
                />
              </div>

              {/* Consulta Image */}
              <div className="form-group">
                <Label className="form-label">
                  {t('consultaDetail.imageLabel')}
                </Label>
                <p className="text-xs text-slate-500 mb-2">{t('newConsulta.imageDesc')}</p>
                {formData.image_url ? (
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-3 pt-1 pr-1">
                      {(formData.image_urls?.length ? formData.image_urls : [formData.image_url]).map((url, imgIdx) => (
                        <div key={imgIdx} className="relative inline-block">
                          <img
                            src={`${API_URL}${url}`}
                            alt={`Imatge votació ${imgIdx + 1}`}
                            className="h-24 w-32 object-contain rounded-lg border border-slate-200 bg-slate-50"
                          />
                          <button
                            type="button"
                            onClick={() => removeConsultaImage(imgIdx)}
                            className="absolute -top-2 -right-2 z-10 bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600 shadow-md cursor-pointer"
                            data-testid={`remove-consulta-image-${imgIdx}`}
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                      {(formData.image_urls?.length || 1) < 6 && (
                        <div>
                          <input
                            ref={consultaImageRef}
                            type="file"
                            accept=".jpg,.jpeg,.png"
                            className="hidden"
                            onChange={(e) => handleConsultaImageUpload(e.target.files[0])}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => consultaImageRef.current?.click()}
                            disabled={uploadingConsultaImage}
                            className="h-24 w-32 border-dashed flex flex-col items-center justify-center gap-1"
                          >
                            {uploadingConsultaImage ? (
                              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                            ) : (
                              <>
                                <ImageIcon className="h-5 w-5 text-slate-400" />
                                <span className="text-xs text-slate-500">+ {t('newConsulta.uploadImage')}</span>
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-slate-400">{(formData.image_urls?.length || 1)}/6</p>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <input
                      ref={consultaImageRef}
                      type="file"
                      accept=".jpg,.jpeg,.png"
                      className="hidden"
                      onChange={(e) => handleConsultaImageUpload(e.target.files[0])}
                      data-testid="consulta-image-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => consultaImageRef.current?.click()}
                      disabled={uploadingConsultaImage}
                      data-testid="upload-consulta-image-btn"
                    >
                      {uploadingConsultaImage ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ImageIcon className="h-4 w-4 mr-2" />}
                      {t('consultaDetail.uploadImage')}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Options with Images */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <Label className="form-label mb-2 block">
                  {t('newConsulta.proposalsTitle')} *
                </Label>
                <p className="text-sm text-slate-500 mb-6">
                  {t('newConsulta.proposalsDesc')}
                </p>
                
                <div className="space-y-6">
                  {formData.options.map((option, index) => (
                    <div 
                      key={index} 
                      className="border border-slate-200 rounded-lg p-4 bg-slate-50"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <span className="font-medium text-slate-700">
                          {t('consultaDetail.proposalNum', { num: index + 1 })}
                        </span>
                        {formData.options.length > 2 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeOption(index)}
                            className="text-slate-400 hover:text-red-500"
                            data-testid={`remove-option-${index}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      
                      {/* Title */}
                      <div className="mb-4">
                        <Label className="text-sm text-slate-600 mb-1 block">
                          {t('consultaDetail.titleRequired')} *
                        </Label>
                        <Input
                          value={option.title}
                          onChange={(e) => handleOptionChange(index, 'title', e.target.value)}
                          placeholder={`Ex: ${t('consultaDetail.proposalNum', { num: index + 1 })}`}
                          className="h-10"
                          data-testid={`option-title-${index}`}
                        />
                      </div>
                      
                      {/* Description */}
                      <div className="mb-4">
                        <Label className="text-sm text-slate-600 mb-1 block">
                          {t('consultaDetail.descriptionLabel')}
                          <span className="ml-2"><MarkdownHelp /></span>
                        </Label>
                        <Textarea
                          value={option.description || ""}
                          onChange={(e) => handleOptionChange(index, 'description', e.target.value)}
                          placeholder={t('consultaDetail.descriptionPlaceholder')}
                          rows={3}
                          data-testid={`option-description-${index}`}
                        />
                      </div>
                      
                      {/* Amount/Cost */}
                      <div className="mb-4">
                        <Label className="text-sm text-slate-600 mb-1 block">
                          {t('consultaDetail.amountLabel')} (€)
                        </Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={option.amount || ""}
                          onChange={(e) => handleOptionChange(index, 'amount', e.target.value)}
                          placeholder="Ex: 15000"
                          className="h-10 w-48"
                          data-testid={`option-amount-${index}`}
                        />
                        <p className="text-xs text-slate-400 mt-1">{t('newConsulta.proposalCost')}</p>
                      </div>
                      
                      {/* Image Upload */}
                      <div>
                        <Label className="text-sm text-slate-600 mb-2 block">
                          {t('consultaDetail.imageLabel')}
                        </Label>
                        
                        {option.image_url ? (
                          <div className="space-y-2">
                            <div className="flex flex-wrap gap-3 pt-1 pr-1">
                              {(option.image_urls?.length ? option.image_urls : [option.image_url]).map((url, imgIdx) => (
                                <div key={imgIdx} className="relative inline-block">
                                  <img 
                                    src={`${API_URL}${url}`}
                                    alt={`Proposta ${index + 1} - ${imgIdx + 1}`}
                                    className="h-24 w-32 object-contain rounded-lg border border-slate-200 bg-slate-50"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removeImage(index, imgIdx)}
                                    className="absolute -top-2 -right-2 z-10 bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600 shadow-md cursor-pointer"
                                    data-testid={`remove-image-${index}-${imgIdx}`}
                                  >
                                    <X className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              ))}
                              {(option.image_urls?.length || 1) < 6 && (
                                <div>
                                  <input
                                    type="file"
                                    accept=".jpg,.jpeg,.png"
                                    ref={el => fileInputRefs.current[index] = el}
                                    onChange={(e) => handleImageUpload(index, e.target.files[0])}
                                    className="hidden"
                                  />
                                  <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => fileInputRefs.current[index]?.click()}
                                    disabled={uploadingIndex === index}
                                    className="h-24 w-32 border-dashed flex flex-col items-center justify-center gap-1"
                                  >
                                    {uploadingIndex === index ? (
                                      <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                                    ) : (
                                      <>
                                        <ImageIcon className="h-5 w-5 text-slate-400" />
                                        <span className="text-xs text-slate-500">+ {t('newConsulta.uploadImage')}</span>
                                      </>
                                    )}
                                  </Button>
                                </div>
                              )}
                            </div>
                            <p className="text-xs text-slate-400">{(option.image_urls?.length || 1)}/6</p>
                          </div>
                        ) : (
                          <div>
                            <input
                              type="file"
                              accept=".jpg,.jpeg,.png"
                              ref={el => fileInputRefs.current[index] = el}
                              onChange={(e) => handleImageUpload(index, e.target.files[0])}
                              className="hidden"
                              data-testid={`image-input-${index}`}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => fileInputRefs.current[index]?.click()}
                              disabled={uploadingIndex === index}
                              className="h-24 w-48 border-dashed flex flex-col items-center justify-center gap-2"
                              data-testid={`upload-image-btn-${index}`}
                            >
                              {uploadingIndex === index ? (
                                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                              ) : (
                                <>
                                  <ImageIcon className="h-6 w-6 text-slate-400" />
                                  <span className="text-sm text-slate-500">{t('newConsulta.uploadImage')}</span>
                                </>
                              )}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <Button
                  type="button"
                  variant="outline"
                  onClick={addOption}
                  className="mt-4"
                  data-testid="add-option-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('consultaDetail.addProposal')}
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Dates */}
          {step === 3 && (
            <div className="space-y-6">
              {/* Start Date & Time */}
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                <Label className="form-label mb-3 block">{t('newConsulta.startDateTime')}</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start h-11 font-normal"
                        data-testid="start-date-btn"
                      >
                        <Calendar className="h-4 w-4 mr-2" />
                        {formData.startDate 
                          ? format(formData.startDate, "PPP", { locale: currentLocale })
                          : t('newConsulta.selectDate')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={formData.startDate}
                        onSelect={(date) => {
                          setFormData({ ...formData, startDate: date });
                          setStartDateOpen(false);
                        }}
                        locale={currentLocale}
                        weekStartsOn={1}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  
                  <div className="relative">
                    <Input
                      type="time"
                      value={formData.startTime}
                      onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                      className="h-11"
                      data-testid="start-time-input"
                    />
                  </div>
                </div>
              </div>

              {/* End Date & Time */}
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                <Label className="form-label mb-3 block">{t('newConsulta.endDateTime')}</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start h-11 font-normal"
                        data-testid="end-date-btn"
                      >
                        <Calendar className="h-4 w-4 mr-2" />
                        {formData.endDate 
                          ? format(formData.endDate, "PPP", { locale: currentLocale })
                          : t('newConsulta.selectDate')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={formData.endDate}
                        onSelect={(date) => {
                          setFormData({ ...formData, endDate: date });
                          setEndDateOpen(false);
                        }}
                        locale={currentLocale}
                        weekStartsOn={1}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  
                  <div className="relative">
                    <Input
                      type="time"
                      value={formData.endTime}
                      onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                      className="h-11"
                      data-testid="end-time-input"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Configuration */}
          {step === 4 && (
            <div className="space-y-6">
              {/* Voting Restriction Settings */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="font-medium text-blue-900 mb-4">{t('newConsulta.votingRestrictions')}</h3>
                
                {/* Restriction Type Selector */}
                <div className="mb-4">
                  <Label className="text-sm font-medium text-blue-800 mb-2 block">
                    {t('consultaDetail.restrictionType')}
                  </Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, restrictionType: 'options' })}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        formData.restrictionType === 'options'
                          ? 'border-blue-500 bg-blue-100'
                          : 'border-slate-200 bg-white hover:border-blue-300'
                      }`}
                      data-testid="restriction-type-options"
                    >
                      <p className="font-medium text-slate-900">{t('newConsulta.byOptionsCount')}</p>
                      <p className="text-xs text-slate-500 mt-1">{t('newConsulta.byOptionsCountDesc')}</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, restrictionType: 'amount' })}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        formData.restrictionType === 'amount'
                          ? 'border-blue-500 bg-blue-100'
                          : 'border-slate-200 bg-white hover:border-blue-300'
                      }`}
                      data-testid="restriction-type-amount"
                    >
                      <p className="font-medium text-slate-900">{t('newConsulta.byMaxAmount')}</p>
                      <p className="text-xs text-slate-500 mt-1">{t('newConsulta.byMaxAmountDesc')}</p>
                    </button>
                  </div>
                </div>

                {/* Options-based restriction */}
                {formData.restrictionType === 'options' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-blue-800 mb-1 block">{t('newConsulta.minOptions')}</Label>
                      <Input
                        type="number"
                        min="1"
                        value={formData.minOptions}
                        onChange={(e) => setFormData({ ...formData, minOptions: e.target.value })}
                        className="h-10"
                        data-testid="min-options-input"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-blue-800 mb-1 block">{t('newConsulta.maxOptions')}</Label>
                      <Input
                        type="number"
                        min="1"
                        max={formData.options.filter(o => o.title.trim()).length || 1}
                        value={formData.maxOptions}
                        onChange={(e) => setFormData({ ...formData, maxOptions: e.target.value })}
                        className="h-10"
                        data-testid="max-options-input"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        {t('newConsulta.maxAllowed', { max: formData.options.filter(o => o.title.trim()).length })}
                      </p>
                    </div>
                  </div>
                )}

                {/* Amount-based restriction */}
                {formData.restrictionType === 'amount' && (
                  <div>
                    <Label className="text-sm text-blue-800 mb-1 block">{t('newConsulta.maxTotalAmount')}</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.maxTotalAmount}
                      onChange={(e) => setFormData({ ...formData, maxTotalAmount: e.target.value })}
                      placeholder="Ex: 50000"
                      className="h-10 w-48"
                      data-testid="max-total-amount-input"
                    />
                    <p className="text-xs text-blue-600 mt-1">
                      {t('consultaDetail.amountHint')}
                    </p>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-slate-900">{t('newConsulta.statsBySex')}</p>
                  <p className="text-sm text-slate-500">
                    {t('newConsulta.statsBySexDesc')}
                  </p>
                </div>
                <Switch
                  checked={formData.enableStatsSex}
                  onCheckedChange={(checked) => 
                    setFormData({ ...formData, enableStatsSex: checked })
                  }
                  data-testid="enable-stats-sex"
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-slate-900">{t('newConsulta.statsByAge')}</p>
                  <p className="text-sm text-slate-500">
                    {t('newConsulta.statsByAgeDesc')}
                  </p>
                </div>
                <Switch
                  checked={formData.enableStatsAge}
                  onCheckedChange={(checked) => 
                    setFormData({ ...formData, enableStatsAge: checked })
                  }
                  data-testid="enable-stats-age"
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-slate-900">{t('newConsulta.requireRegistration')}</p>
                  <p className="text-sm text-slate-500">
                    {t('newConsulta.requireRegistrationDesc')}
                  </p>
                </div>
                <Switch
                  checked={formData.requireRegistration}
                  onCheckedChange={(checked) => 
                    setFormData({ ...formData, requireRegistration: checked })
                  }
                  data-testid="require-registration"
                />
              </div>

              {/* === VOTING MODE SECTION === */}
              <div className="border-t border-slate-200 pt-6">
                <h3 className="font-semibold text-slate-900 text-base mb-1" data-testid="voting-mode-section-title">
                  {t('newConsulta.stepConfig')}
                </h3>
                <p className="text-sm text-slate-500 mb-4">{t('votingMode.global.footer')}</p>

                {/* Anonymous (default) info panel - always visible */}
                <div className={`p-4 rounded-lg border-2 mb-4 transition-all ${
                  !formData.enableVotMaAlcada && !formData.enableVoterTracking
                    ? 'border-emerald-400 bg-emerald-50'
                    : 'border-slate-200 bg-slate-50 opacity-60'
                }`} data-testid="mode-panel-anonymous">
                  <div className="flex items-start gap-3">
                    <ShieldCheck className={`h-5 w-5 mt-0.5 flex-shrink-0 ${
                      !formData.enableVotMaAlcada && !formData.enableVoterTracking ? 'text-emerald-600' : 'text-slate-400'
                    }`} />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-900">{t('votingMode.anon.title')}</p>
                        {!formData.enableVotMaAlcada && !formData.enableVoterTracking && (
                          <span className="text-xs bg-emerald-200 text-emerald-800 px-2 py-0.5 rounded-full font-medium">
                            {t('votingMode.activeMode')}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 mt-1">{t('votingMode.anon.shortDesc')}</p>
                      {!formData.enableVotMaAlcada && !formData.enableVoterTracking && (
                        <div className="mt-3 space-y-1 text-sm text-slate-600">
                          <p>{t('votingMode.anon.longDesc1')}</p>
                          <p>{t('votingMode.anon.longDesc2')}</p>
                          <p>{t('votingMode.anon.longDesc3')}</p>
                          <p className="text-emerald-700 font-medium mt-2 text-xs">{t('votingMode.anon.warning')}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Hand Vote switch + panel */}
                <div className={`p-4 rounded-lg border-2 mb-4 transition-all ${
                  formData.enableVotMaAlcada ? 'border-orange-400 bg-orange-50' : 'border-slate-200 bg-slate-50'
                }`} data-testid="mode-panel-hand-vote">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1">
                      <Eye className={`h-5 w-5 mt-0.5 flex-shrink-0 ${
                        formData.enableVotMaAlcada ? 'text-orange-600' : 'text-slate-400'
                      }`} />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900">{t('votingMode.handVote.title')}</p>
                          {formData.enableVotMaAlcada && (
                            <span className="text-xs bg-orange-200 text-orange-800 px-2 py-0.5 rounded-full font-medium">
                              {t('votingMode.activeMode')}
                            </span>
                          )}
                          <div className="relative group">
                            <Info className="h-4 w-4 text-slate-400 cursor-help" />
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 p-2 bg-slate-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                              {t('votingMode.tooltip.handVote')}
                            </div>
                          </div>
                        </div>
                        <p className="text-sm text-slate-600 mt-1">{t('votingMode.handVote.shortDesc')}</p>
                      </div>
                    </div>
                    <Switch
                      checked={formData.enableVotMaAlcada}
                      onCheckedChange={(checked) => 
                        setFormData({ ...formData, enableVotMaAlcada: checked, ...(checked ? { enableAudit: false, enableVoterTracking: false, enablePrivateAudit: false } : {}) })
                      }
                      disabled={formData.enableAudit || formData.enableVoterTracking || formData.enablePrivateAudit}
                      data-testid="enable-vot-ma-alcada"
                    />
                  </div>
                  {formData.enableVotMaAlcada && (
                    <div className="mt-3 ml-8 space-y-1 text-sm text-slate-600">
                      <p>{t('votingMode.handVote.longDesc1')}</p>
                      <p>{t('votingMode.handVote.longDesc2')}</p>
                      <p>{t('votingMode.handVote.longDesc3')}</p>
                      <p className="text-orange-700 font-medium mt-2 text-xs">{t('votingMode.handVote.warning')}</p>
                    </div>
                  )}
                  {(formData.enableAudit || formData.enablePrivateAudit || formData.enableVoterTracking) && !formData.enableVotMaAlcada && (
                    <p className="text-xs text-amber-600 mt-2 ml-8">{t('newConsulta.incompatibleWithAudit')}</p>
                  )}
                </div>

                {/* Voter Tracking switch + panel */}
                <div className={`p-4 rounded-lg border-2 mb-4 transition-all ${
                  formData.enableVoterTracking ? 'border-amber-400 bg-amber-50' : 'border-slate-200 bg-slate-50'
                }`} data-testid="mode-panel-tracking">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1">
                      <Users className={`h-5 w-5 mt-0.5 flex-shrink-0 ${
                        formData.enableVoterTracking ? 'text-amber-600' : 'text-slate-400'
                      }`} />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900">{t('votingMode.tracking.title')}</p>
                          {formData.enableVoterTracking && (
                            <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full font-medium">
                              {t('votingMode.activeMode')}
                            </span>
                          )}
                          <div className="relative group">
                            <Info className="h-4 w-4 text-slate-400 cursor-help" />
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 p-2 bg-slate-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                              {t('votingMode.tooltip.tracking')}
                            </div>
                          </div>
                        </div>
                        <p className="text-sm text-slate-600 mt-1">{t('votingMode.tracking.shortDesc')}</p>
                      </div>
                    </div>
                    <Switch
                      checked={formData.enableVoterTracking}
                      onCheckedChange={(checked) => 
                        setFormData({ ...formData, enableVoterTracking: checked, ...(checked ? { enableVotMaAlcada: false } : {}) })
                      }
                      disabled={formData.enableVotMaAlcada}
                      data-testid="enable-voter-tracking"
                    />
                  </div>
                  {formData.enableVoterTracking && (
                    <div className="mt-3 ml-8">
                      <div className="space-y-1 text-sm text-slate-600">
                        <p>{t('votingMode.tracking.longDesc1')}</p>
                        <p>{t('votingMode.tracking.longDesc2')}</p>
                        <p className="text-amber-700 font-medium mt-2 text-xs">{t('votingMode.tracking.warning')}</p>
                      </div>
                      {/* Voter preview */}
                      <div className="mt-3 p-3 bg-white rounded-lg border border-amber-200">
                        <p className="text-xs font-medium text-amber-800 mb-1">{t('votingMode.voterWillSee')}:</p>
                        <p className="text-sm font-medium text-amber-700">{t('votingMode.tracking.voterMessage')}</p>
                        <p className="text-xs text-slate-600 mt-1">{t('votingMode.tracking.voterExplanation')}</p>
                      </div>
                    </div>
                  )}
                  {formData.enableVotMaAlcada && !formData.enableVoterTracking && (
                    <p className="text-xs text-amber-600 mt-2 ml-8">{t('newConsulta.incompatibleWithHandVote')}</p>
                  )}
                </div>

                {/* Audit (kept as simple switch) */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border-2 border-slate-200 mb-4">
                  <div>
                    <p className="font-medium text-slate-900">{t('newConsulta.audit')}</p>
                    <p className="text-sm text-slate-500">
                      {t('newConsulta.auditDesc')}
                    </p>
                    {formData.enableVotMaAlcada && (
                      <p className="text-xs text-amber-600 mt-1">{t('newConsulta.incompatibleWithHandVote')}</p>
                    )}
                    {formData.enablePrivateAudit && (
                      <p className="text-xs text-amber-600 mt-1">{t('newConsulta.incompatibleWithPrivateAudit')}</p>
                    )}
                  </div>
                  <Switch
                    checked={formData.enableAudit}
                    onCheckedChange={(checked) => 
                      setFormData({ ...formData, enableAudit: checked, ...(checked ? { enableVotMaAlcada: false, enablePrivateAudit: false } : {}) })
                    }
                    disabled={formData.enableVotMaAlcada || formData.enablePrivateAudit}
                    data-testid="enable-audit"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6 border-t">
            <Button
              variant="outline"
              onClick={() => setStep(step - 1)}
              disabled={step === 1}
              data-testid="prev-step-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('newConsulta.previous')}
            </Button>

            {step < 4 ? (
              <Button
                onClick={handleNext}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="next-step-btn"
              >
                {t('newConsulta.next')}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="create-consulta-submit"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('common.createConsulta')}
              </Button>
            )}
          </div>
        </motion.div>
      </div>
    </Layout>
  );
}
